/**
 * The signed-in admin's own profile (M24-T02).
 *
 * **Why this is a query and not part of `AuthContext`.** The ticket's first
 * sketch put the fetch in `AuthContext`, which reads well until you try it:
 * `AuthContext.test.tsx` and `LoginPage.test.tsx` both render `AuthProvider`
 * with **no** `QueryClientProvider`, so a `useQuery` inside the provider would
 * break the two suites that cover signing in — and hand-rolling a second
 * fetch-and-cache next to React Query would be worse. Identity is read where it
 * is displayed instead.
 *
 * It is also not a gate: `AdminShell` renders its card from `AuthContext`'s
 * role whether or not this query has landed. Nobody should wait on a name to
 * see their queues.
 */
import { apiFetch } from './client'

export type AdminProfile = {
  user_id: string
  role: string
  /** Null for every seeded hospital admin — the specified state, not a gap. */
  full_name: string | null
  email: string | null
}

export const PROFILE_KEY = ['admin', 'me'] as const

/** GET /api/v1/me — id and role from the token's own row, plus the two
 *  profile columns the route reads on demand (ADR-0050). */
export async function fetchAdminProfile(
  token: string | null,
): Promise<AdminProfile> {
  return await apiFetch<AdminProfile>('/api/v1/me', { token })
}

/**
 * Initials for the avatar: first letter of the first word, first letter of the
 * last word, at most two.
 *
 * Returns `null` when there is no name, and the caller shows the generic glyph.
 * **It never falls back to the email.** `ops@amtc.org` rendering as "O" invents
 * a person from a mailbox — the frame's own `admin@hotel.com` defect (D12) in
 * miniature, and ADR-0050 rules it out explicitly.
 */
export function initialsFor(
  fullName: string | null | undefined,
): string | null {
  if (!fullName) return null
  const words = fullName.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return null
  const first = words[0]?.[0] ?? ''
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : ''
  const out = (first + last).toUpperCase()
  return out === '' ? null : out
}

/**
 * The Self tier's write surface (M24-T08). Exactly two fields, plus the current
 * password when the email moves.
 *
 * `current_password` is required by the server **only** when `email` changes,
 * because the email is the login credential and the product cannot send a
 * confirmation to the new address. Changing only the name needs nothing.
 */
export type UpdateProfileBody = {
  full_name?: string
  email?: string
  current_password?: string
}

/** PATCH /api/v1/me. 409 `email_taken`, 401 `invalid_credentials`. */
export async function updateAdminProfile(
  token: string | null,
  body: UpdateProfileBody,
): Promise<AdminProfile> {
  return await apiFetch<AdminProfile>('/api/v1/me', {
    token,
    method: 'PATCH',
    body,
  })
}

/** POST /api/v1/me/password. 204 on success; 401 `invalid_credentials`. */
export async function changeAdminPassword(
  token: string | null,
  body: { current_password: string; new_password: string },
): Promise<void> {
  await apiFetch<void>('/api/v1/me/password', {
    token,
    method: 'POST',
    body,
  })
}
